import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';

const PLAN_COLORS: Record<string, string> = {
  free: '#8892A4', basic: '#00D4AA', standard: '#378ADD', pro: '#F6AD55'
};

export default function Admin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'stats' | 'users' | 'notices' | 'ip' | 'api' | 'settings'>('stats');
  const [stats, setStats] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [notices, setNotices] = useState<any[]>([]);
  const [noticeTitle, setNoticeTitle] = useState('');
  const [noticeContent, setNoticeContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [ipList, setIpList] = useState<any[]>([]);
  const [settings, setSettings] = useState<any[]>([]);
  const [apiUsage, setApiUsage] = useState<any[]>([]);
  const [newIp, setNewIp] = useState('');
  const [newIpPlan, setNewIpPlan] = useState('pro');
  const [newIpDesc, setNewIpDesc] = useState('');

  // 관리자 확인
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user.is_admin) { navigate('/login'); return; }
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [s, u, n, ip, st, a2] = await Promise.all([
        client.get('/admin/stats'),
        client.get('/admin/users'),
        client.get('/admin/notices'),
        client.get('/admin/ip-whitelist'),
        client.get('/admin/settings'),
        client.get('/admin/api-usage'),
      ]);
      setStats(s.data);
      setUsers(u.data.users);
      setNotices(n.data.notices);
      setIpList(ip.data.ips || []);
      if (st) setSettings(st.data.settings || []);
      setApiUsage(a2.data.usage || []);
    } catch { navigate('/login'); }
  };

  const handlePlanChange = async (user_id: number, plan: string) => {
    await client.put('/admin/users/plan', { user_id, plan });
    await loadAll();
  };

  const handleDeleteUser = async (user_id: number, name: string) => {
    if (!window.confirm(`${name} 유저를 강제 탈퇴시킬까요?`)) return;
    await client.delete(`/admin/users/${user_id}`);
    await loadAll();
  };

  const handleAddNotice = async () => {
    if (!noticeTitle.trim() || !noticeContent.trim()) return alert('제목과 내용을 입력해주세요');
    await client.post('/admin/notices', { title: noticeTitle, content: noticeContent });
    setNoticeTitle(''); setNoticeContent('');
    await loadAll();
  };

  const handleAddIp = async () => {
    if (!newIp.trim()) return alert('IP를 입력해주세요');
    try {
      await client.post('/admin/ip-whitelist', { ip: newIp.trim(), plan: newIpPlan, description: newIpDesc });
      setNewIp(''); setNewIpDesc('');
      await loadAll();
    } catch (e: any) { alert(e.response?.data?.detail || '추가 실패'); }
  };

  const handleDeleteIp = async (id: number) => {
    if (!window.confirm('삭제할까요?')) return;
    await client.delete(`/admin/ip-whitelist/${id}`);
    await loadAll();
  };

  const s: any = {
    page: { minHeight: '100vh', background: '#0F1117', padding: 24 },
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 16 },
    tab: (active: boolean) => ({ background: active ? '#1A2035' : 'none', border: active ? '0.5px solid #00D4AA' : '0.5px solid #2A3045', borderRadius: 6, padding: '7px 20px', color: active ? '#00D4AA' : '#8892A4', fontSize: 13, cursor: 'pointer', fontWeight: active ? 700 : 400 }),
    btn: { background: '#00D4AA', border: 'none', borderRadius: 6, padding: '7px 16px', color: '#0A1F18', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
    btnRed: { background: '#F56565', border: 'none', borderRadius: 6, padding: '5px 12px', color: '#fff', fontSize: 11, cursor: 'pointer' },
    input: { background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 6, padding: '8px 12px', color: '#E8ECF4', fontSize: 13, width: '100%', boxSizing: 'border-box' },
  };

  return (
    <div style={s.page}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* 헤더 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <div style={{ color: '#F56565', fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', marginBottom: 4 }}>ADMIN</div>
            <div style={{ color: '#E8ECF4', fontSize: 20, fontWeight: 700 }}>SellerRanky 관리자</div>
          </div>
          <button onClick={() => navigate('/dashboard')} style={{ ...s.btn, background: 'none', border: '0.5px solid #2A3045', color: '#8892A4' }}>← 대시보드</button>
        </div>

        {/* 탭 */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <button style={s.tab(tab === 'stats')} onClick={() => setTab('stats')}>📊 통계</button>
          <button style={s.tab(tab === 'users')} onClick={() => setTab('users')}>👥 유저 관리 ({users.length})</button>
          <button style={s.tab(tab === 'notices')} onClick={() => setTab('notices')}>📢 공지사항</button>
          <button style={s.tab(tab === 'ip')} onClick={() => setTab('ip')}>🌐 IP 관리 ({ipList.length})</button>
          <button style={s.tab(tab === 'api')} onClick={() => setTab('api')}>📊 API 사용량</button>
          <button style={s.tab(tab === 'settings')} onClick={() => setTab('settings')}>⚙️ 시스템 설정</button>
        </div>

        {/* 통계 탭 */}
        {tab === 'stats' && stats && (
          <>
            {/* 핵심 지표 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
              {[
                { label: '오늘 방문자', value: stats.today_visitors ?? 0, color: '#00D4AA' },
                { label: '오늘 방문 회원', value: stats.today_member_visitors ?? 0, color: '#00B8D4' },
                { label: '오늘 신규 가입', value: stats.today_signups ?? 0, color: '#378ADD' },
                { label: '오늘 유료 전환', value: stats.today_paid_conversions ?? 0, color: '#F6AD55' },
                { label: '전체 가입자', value: stats.total_users, color: '#8892A4' },
                { label: '유료 회원', value: stats.total_paid, color: '#F56565' },
              ].map(c => (
                <div key={c.label} style={s.card}>
                  <div style={{ color: '#4A5568', fontSize: 10, textTransform: 'uppercase' as const, marginBottom: 6 }}>{c.label}</div>
                  <div style={{ color: c.color, fontSize: 24, fontWeight: 700 }}>{typeof c.value === 'number' ? c.value.toLocaleString() : c.value}</div>
                </div>
              ))}
            </div>

            {/* 일별 방문자/가입자 테이블 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div style={s.card}>
                <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>일별 방문자 (최근 30일)</div>
                <div style={{ maxHeight: 200, overflowY: 'auto' as const }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' as const }}>
                    <thead>
                      <tr>
                        <th style={{ color: '#4A5568', fontSize: 11, textAlign: 'left' as const, padding: '4px 0' }}>날짜</th>
                        <th style={{ color: '#4A5568', fontSize: 11, textAlign: 'right' as const, padding: '4px 0' }}>방문자</th>
                        <th style={{ color: '#4A5568', fontSize: 11, textAlign: 'right' as const, padding: '4px 0' }}>가입</th>
                        <th style={{ color: '#4A5568', fontSize: 11, textAlign: 'right' as const, padding: '4px 0' }}>전환율</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(stats.daily_visits || []).map((v: any) => {
                        const signup = (stats.daily_signups || []).find((s: any) => s.date === v.date);
                        const signupCount = signup?.signups || 0;
                        const rate = v.visitors > 0 ? Math.round(signupCount / v.visitors * 100) : 0;
                        return (
                          <tr key={v.date} style={{ borderBottom: '0.5px solid #1E2535' }}>
                            <td style={{ color: '#8892A4', fontSize: 12, padding: '5px 0' }}>{v.date}</td>
                            <td style={{ color: '#00D4AA', fontSize: 12, textAlign: 'right' as const }}>{v.visitors}</td>
                            <td style={{ color: '#378ADD', fontSize: 12, textAlign: 'right' as const }}>{signupCount}</td>
                            <td style={{ color: '#F6AD55', fontSize: 12, textAlign: 'right' as const }}>{rate}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={s.card}>
                <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>플랜별 현황</div>
                {Object.entries(stats.plan_stats || {}).map(([plan, count]: any) => {
                  const inactive = (stats.inactive || []).find((i: any) => i.plan === plan);
                  return (
                    <div key={plan} style={{ padding: '8px 0', borderBottom: '0.5px solid #1E2535' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ color: PLAN_COLORS[plan] || '#8892A4', fontSize: 13, fontWeight: 600 }}>{plan}</span>
                        <span style={{ color: '#E8ECF4', fontSize: 13 }}>{count}명</span>
                      </div>
                      {inactive && (
                        <div style={{ color: '#F56565', fontSize: 11 }}>30일 미접속: {inactive.count}명</div>
                      )}
                    </div>
                  );
                })}
                <div style={{ marginTop: 16 }}>
                  <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>기능별 누적 사용량</div>
                  {[
                    { label: '키워드 추적 등록', value: stats.total_keywords },
                    { label: '순위 조회', value: stats.total_rank_checks },
                    { label: '관여도 진단', value: stats.total_involvements },
                    { label: '마케팅 전략', value: stats.total_strategies },
                    { label: '키워드 전략', value: stats.total_keyword_strategies },
                    { label: '플레이스 순위 조회', value: stats.total_place_rank },
                    { label: '플레이스 등록', value: stats.total_place_places },
                    { label: '원부 추적 등록', value: stats.total_catalog_trackers },
                    { label: '원부 순위 조회', value: stats.total_catalog_checks },
                  ].map(item => (
                    <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '0.5px solid #1E2535' }}>
                      <span style={{ color: '#8892A4', fontSize: 13 }}>{item.label}</span>
                      <span style={{ color: '#E8ECF4', fontSize: 13 }}>{(item.value || 0).toLocaleString()}회</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* 유저 관리 탭 */}
        {tab === 'users' && (
          <div style={s.card}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#1A2035' }}>
                  {['ID', '이메일', '이름', '플랜', '상품수', '가입일', '마지막접속', '플랜변경', '탈퇴'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#8892A4', fontWeight: 500, fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ borderBottom: '0.5px solid #1E2535' }}>
                    <td style={{ padding: '8px 10px', color: '#4A5568', fontSize: 11 }}>{u.id}</td>
                    <td style={{ padding: '8px 10px', color: '#E8ECF4' }}>{u.email}</td>
                    <td style={{ padding: '8px 10px', color: '#E8ECF4' }}>{u.name}</td>
                    <td style={{ padding: '8px 10px' }}>
                      <span style={{ color: PLAN_COLORS[u.plan] || '#8892A4', fontWeight: 600 }}>{u.plan}</span>
                    </td>
                    <td style={{ padding: '8px 10px', color: '#8892A4' }}>{u.product_count}</td>
                    <td style={{ padding: '8px 10px', color: '#4A5568', fontSize: 11 }}>{u.created_at?.slice(0, 10)}</td>
                    <td style={{ padding: '8px 10px', color: '#4A5568', fontSize: 11 }}>{u.updated_at?.slice(0, 10)}</td>
                    <td style={{ padding: '8px 10px' }}>
                      <select value={u.plan} onChange={e => handlePlanChange(u.id, e.target.value)}
                        style={{ background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 4, color: '#E8ECF4', fontSize: 11, padding: '3px 6px', cursor: 'pointer' }}>
                        {['free', 'basic', 'standard', 'pro'].map(p => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <button style={s.btnRed} onClick={() => handleDeleteUser(u.id, u.name)}>탈퇴</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 공지사항 탭 */}
        {tab === 'notices' && (
          <>
            <div style={s.card}>
              <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>공지사항 등록</div>
              <input style={{ ...s.input, marginBottom: 8 }} value={noticeTitle} onChange={e => setNoticeTitle(e.target.value)} placeholder="제목" />
              <textarea style={{ ...s.input, height: 80, resize: 'vertical', marginBottom: 12 }} value={noticeContent} onChange={e => setNoticeContent(e.target.value)} placeholder="내용" />
              <button style={s.btn} onClick={handleAddNotice}>등록</button>
            </div>
            {notices.map(n => (
              <div key={n.id} style={s.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{n.title}</div>
                    <div style={{ color: '#8892A4', fontSize: 13, marginBottom: 4, whiteSpace: 'pre-wrap' }}>{n.content}</div>
                    <div style={{ color: '#4A5568', fontSize: 11 }}>{n.created_at?.slice(0, 16)}</div>
                  </div>
                  <button style={s.btnRed} onClick={async () => { await client.delete(`/admin/notices/${n.id}`); await loadAll(); }}>삭제</button>
                </div>
              </div>
            ))}
          </>
        )}

        {/* IP 화이트리스트 탭 */}
        {tab === 'ip' && (
          <>
            <div style={s.card}>
              <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>IP 등록</div>
              <div style={{ color: '#8892A4', fontSize: 12, marginBottom: 12 }}>등록된 IP에서 접속하는 모든 계정은 설정한 플랜으로 이용 가능합니다</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: 8, alignItems: 'center' }}>
                <input style={s.input} value={newIp} onChange={e => setNewIp(e.target.value)}
                  placeholder="IP 주소 (예: 121.154.92.3)" />
                <select value={newIpPlan} onChange={e => setNewIpPlan(e.target.value)}
                  style={{ background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 6, color: '#E8ECF4', fontSize: 13, padding: '8px 12px', cursor: 'pointer' }}>
                  {['basic', 'standard', 'pro'].map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                <input style={{ ...s.input, width: 160 }} value={newIpDesc} onChange={e => setNewIpDesc(e.target.value)}
                  placeholder="설명 (예: 학교 WiFi)" />
                <button style={s.btn} onClick={handleAddIp}>추가</button>
              </div>
            </div>

            <div style={s.card}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#1A2035' }}>
                    {['IP', '플랜', '설명', '등록일', '상태', '삭제'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#8892A4', fontSize: 12, fontWeight: 500 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ipList.length === 0 ? (
                    <tr><td colSpan={6} style={{ padding: '20px', textAlign: 'center', color: '#4A5568' }}>등록된 IP가 없습니다</td></tr>
                  ) : ipList.map((ip: any) => (
                    <tr key={ip.id} style={{ borderBottom: '0.5px solid #1E2535' }}>
                      <td style={{ padding: '8px 10px', color: '#E8ECF4', fontFamily: 'monospace' }}>{ip.ip}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <span style={{ color: PLAN_COLORS[ip.plan] || '#8892A4', fontWeight: 600 }}>{ip.plan}</span>
                      </td>
                      <td style={{ padding: '8px 10px', color: '#8892A4' }}>{ip.description || '-'}</td>
                      <td style={{ padding: '8px 10px', color: '#4A5568', fontSize: 11 }}>{ip.created_at?.slice(0, 10)}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <span style={{ color: ip.is_active ? '#00D4AA' : '#F56565', fontSize: 12 }}>{ip.is_active ? '활성' : '비활성'}</span>
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <button style={s.btnRed} onClick={() => handleDeleteIp(ip.id)}>삭제</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 'api' && (
          <div style={s.card}>
            <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>오늘 API 사용량 (유저별)</div>
            <div style={{ color: '#8892A4', fontSize: 12, marginBottom: 16 }}>오늘 날짜 기준 유저별 기능별 실행 횟수 (실행버튼 1회 = 1회)</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#1A2035' }}>
                  {['이메일', '플랜', '키워드', '플레이스', '관여도', '마케팅', '형태소', '키워드전략', '쇼핑순위', '총계'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#8892A4', fontSize: 12, fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {apiUsage.map((u: any) => (
                  <tr key={u.id} style={{ borderBottom: '0.5px solid #1E2535' }}>
                    <td style={{ padding: '8px 10px', color: '#E8ECF4' }}>{u.email}</td>
                    <td style={{ padding: '8px 10px', color: PLAN_COLORS[u.plan] || '#8892A4', fontWeight: 600 }}>{u.plan}</td>
                    <td style={{ padding: '8px 10px', color: '#8892A4' }}>{u.keyword_count || 0}</td>
                    <td style={{ padding: '8px 10px', color: '#8892A4' }}>{u.place_count || 0}</td>
                    <td style={{ padding: '8px 10px', color: '#8892A4' }}>{u.involvement_count || 0}</td>
                    <td style={{ padding: '8px 10px', color: '#8892A4' }}>{u.marketing_count || 0}</td>
                    <td style={{ padding: '8px 10px', color: '#8892A4' }}>{u.morpheme_count || 0}</td>
                    <td style={{ padding: '8px 10px', color: '#8892A4' }}>{u.keyword_strategy_count || 0}</td>
                    <td style={{ padding: '8px 10px', color: '#8892A4' }}>{u.shopping_rank_count || 0}</td>
                    <td style={{ padding: '8px 10px', color: u.total_count > 0 ? '#00D4AA' : '#4A5568', fontWeight: 600 }}>{u.total_count || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 시스템 설정 탭 */}
        {tab === 'settings' && (
          <div style={s.card}>
            <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>⚙️ 시스템 설정</div>
            {settings.length === 0 ? (
              <div style={{ color: '#4A5568', fontSize: 13 }}>설정 항목이 없습니다</div>
            ) : settings.map((setting: any) => (
              <div key={setting.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '0.5px solid #1E2535' }}>
                <div>
                  <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600 }}>
                    {setting.key === 'plan_limit_enabled' ? '플랜 기능 제한' : setting.key}
                  </div>
                  <div style={{ color: '#4A5568', fontSize: 12, marginTop: 2 }}>
                    {setting.key === 'plan_limit_enabled' ? 'ON: 플랜별 기능 제한 적용 / OFF: 모든 유저 무제한' : setting.description}
                  </div>
                </div>
                <div
                  onClick={async () => {
                    const newVal = setting.value === 'true' ? 'false' : 'true';
                    await client.put('/admin/settings', { key: setting.key, value: newVal });
                    setSettings(prev => prev.map((s: any) => s.key === setting.key ? { ...s, value: newVal } : s));
                  }}
                  style={{ width: 44, height: 24, borderRadius: 12, background: setting.value === 'true' ? '#00D4AA' : '#2A3045', position: 'relative' as const, cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}
                >
                  <div style={{ position: 'absolute' as const, top: 2, left: setting.value === 'true' ? 22 : 2, width: 20, height: 20, borderRadius: 10, background: 'white', transition: 'left 0.2s' }} />
                </div>
              </div>
            ))}
            <div style={{ marginTop: 16, padding: '12px 14px', background: '#0F1117', borderRadius: 8 }}>
              <div style={{ color: '#F6AD55', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>⚠️ 주의</div>
              <div style={{ color: '#8892A4', fontSize: 12, lineHeight: 1.8 }}>
                기능 제한 OFF: 모든 유저 무제한 사용 가능 (테스트/오픈베타)<br />
                기능 제한 ON: 플랜별 제한 적용 (실제 서비스)
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
